// accounting-group.ts — Accounting page (Phase 12.12).
//
// The "Accounting" nav item. It mirrors the per-terminal
// "openTerminal > Accounting > Ranged" sub-tab but at the OPERATOR
// GROUP level: the user picks a group (or the only group they belong
// to, in the operator case) and a date range, and the table below the
// calendar shows the SUM of acc_diff across every mid in that group
// for every day in the range.
//
// Data source: GET /api/groupacc?oid=N&from=YYYY-MM-DD&to=YYYY-MM-DD
// (admin/api/index.php). The server already aggregates per-day
// acc_diff across every mid, so the client just sums those aggregates
// with the same sumAccs() helper the per-terminal Ranged tab uses.
// Range coalescing is unnecessary — the server's range query is one
// call per range change, period.
//
// Currency table: each terminal has its own cur_recv / currencyLookup
// (Hopper 1 might accept CZK+EUR, Hopper 2 might accept CZK only,
// etc.). At the group level we UNION every terminal's currencyLookup
// into a synthetic "group context" so renderAccTable() can show one
// column per ISO in use across the whole group. Currencies that
// weren't used by a particular mid on a particular day show as 0.
//
// Range bounds:
//   - lower: 90 days back (defensive default; we have no group-level
//     "earliest activity" date in a single column).
//   - upper: today (no future dates).
//
// Role scoping: an operator with op_id=N is locked to that group on
// the server side (groupacc returns 403 otherwise). The select only
// shows groups the operator is allowed to see; an admin sees all
// groups.

import { apiFetch, type ApiFetchConfig } from './api';
import {
  normaliseAccounting,
  renderAccTable,
  makeAccHeader,
  type NormalisedAcc,
} from './accounting';
// sumAccs lives in the per-terminal Ranged module. It's a pure
// helper that doesn't depend on Terminal state, so we import it
// from there. (Re-implementing it here would be duplication.)
import { sumAccs as sumAccsFromAcc } from './accounting-ranged';
// Phase 12.14b: charts below the group table.
import { destroyGroupCharts, renderGroupCharts } from './group-charts';
import { buildCurrencyLookup, getCurrencyByISO } from './utils/currency';
import type { TimestopState } from './state';
import type { Terminal, CurrencyConfig } from './types';
import { showSpinner, hideSpinner, setSpinnerMessage } from './spinner';
import { t as i18n } from './i18n';

// ---- Module-scope state for the currently-open Accounting page ----
interface GroupRangedState {
  // Selected group id (string so it round-trips through HTML values).
  oid: string | null;
  from: string | null;       // 'YYYY-MM-DD'
  to:   string | null;
  // Per-day cache, keyed by YYYY-MM-DD. We cache the NormalisedAcc
  // AND the per-day mid_count (unique to /api/groupacc) so the
  // chart code can read both in one .get() without a parallel map.
  // gamesByMid is the per-mid [games1, games2, games3] array keyed by
  // stringified mid id — used to draw one bar per terminal without
  // re-fetching.
  cache: Map<string, {
    acc: NormalisedAcc;
    midCount: number;
    gamesByMid: Record<string, number[]>;
  }>;
  // Calendar's leftmost visible month ('YYYY-MM-01').
  leftMonth: string;
  // Last fetch timestamp so we can show a "fetched" badge.
  lastFetchedAt: string | null;
}
let _state: GroupRangedState | null = null;

function getState(): GroupRangedState {
  if (!_state) {
    _state = {
      oid: null,
      from: null,
      to:   null,
      cache: new Map(),
      leftMonth: currentMonthStart(),
      lastFetchedAt: null,
    };
  }
  return _state;
}

// Cleared by navigate(6)'s lazy-load path so re-entering the page
// starts with an empty cache + no orphan range. Same pattern as
// clearRangedState() in accounting-ranged.ts.
export function clearGroupAccountingState(): void {
  // Phase 12.14b: also release chart instances. Defensive — the
  // top of renderAccountingGroup() already calls destroyGroupCharts
  // on every entry, so calling it here too is belt-and-suspenders
  // for the case where someone clears state without a re-render
  // (e.g. a future "back to groups list" button).
  try { destroyGroupCharts(); } catch { /* already cleared */ }
  _state = null;
}

// ---- Date helpers (mirror accounting-ranged.ts) ----
function pad2(n: number): string { return (n < 10 ? '0' : '') + n; }
function ymd(d: Date): string { return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); }
function currentMonthStart(): string {
  const d = new Date();
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-01';
}
function parseYmd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function addDays(s: string, n: number): string {
  const d = parseYmd(s);
  d.setDate(d.getDate() + n);
  return ymd(d);
}
function addMonths(s: string, n: number): string {
  const d = parseYmd(s + 'T00:00:00');
  d.setMonth(d.getMonth() + n);
  return ymd(d).slice(0, 7) + '-01';
}
function cmpYmd(a: string, b: string): number { return a < b ? -1 : a > b ? 1 : 0; }
function minYmd(a: string, b: string): string { return cmpYmd(a, b) <= 0 ? a : b; }
function maxYmd(a: string, b: string): string { return cmpYmd(a, b) >= 0 ? a : b; }

// ---- Group context builder ----
// Build a synthetic Terminal that renderAccTable() can consume, whose
// currencyLookup is the UNION of every terminal in the group PLUS
// every ISO that actually appears in the picked range's NormalisedAcc.
// This last bit matters: terminals often report sales in currencies
// the operator group has NEVER formally configured in their cur_recv
// (an old spot-terminal config leaks a PLN sale into a group that now
// only sells EUR, etc.). Without the data-derived union, the table
// silently drops those columns. We only need the currencyLookup
// populated; the rest of the fields are placeholders.
//
// Pass `dataFromRange` (the NormalisedAcc you'll hand to
// renderAccTable) when available so the union includes every ISO
// the data has. Pass null when no range has been picked yet (the
// placeholder path).
function buildGroupContext(
  state: TimestopState,
  oid: string | number,
  dataFromRange: { in?: { coin?: Record<string, number>; note?: Record<string, number> } } | null = null,
): Terminal {
  const t = (Object.values(state.terminals) as Terminal[]).filter(
    (term) => String(term.oid || '') === String(oid),
  );
  const union: Record<string, CurrencyConfig> = {};
  for (const term of t) {
    // Some terminals lazy-init currencyLookup when first opened in
    // the modal; if it's missing, build it from cur_recv now.
    let lu = term.currencyLookup;
    if (!lu && term.cur_recv) {
      lu = buildCurrencyLookup(term.cur_recv);
      term.currencyLookup = lu;
    }
    if (!lu) continue;
    for (const [iso, cur] of Object.entries(lu)) {
      if (!union[iso]) union[iso] = cur;
    }
  }
  // Also union in every ISO that appears in the data. For each one
  // we try the BO-wide currencyDefaults table (loaded from
  // /api/currencies at login) first — if the admin set a non-default
  // div/fs for that ISO (e.g. JPY with div:1, KWD with div:1000) we
  // want that. The synthetic Terminal we hand to getCurrencyByISO
  // has no cur_recv, so it falls straight through to state.
  //
  // If the admin didn't configure the ISO, getCurrencyByISO returns
  // its hard-coded {div:100, fs:'%.2f '+iso} placeholder — the
  // same shape the old inline code used, so the column renders
  // identically to before.
  if (dataFromRange) {
    const synthetic: Terminal = { id: '__synth', name: '', cur_recv: undefined };
    const inMap = dataFromRange.in || {};
    for (const ch of ['coin', 'note'] as const) {
      const m = (inMap as Record<string, Record<string, number>>)[ch] || {};
      for (const iso of Object.keys(m)) {
        if (!union[iso]) {
          union[iso] = getCurrencyByISO(synthetic, iso, state);
        }
      }
    }
  }
  return {
    id: '__group_' + oid,
    name: 'Group ' + oid,
    currencyLookup: union,
  };
}

// ---- Available groups helper ----
// Returns the list of groups the user is allowed to query. Admins
// see all groups from state.groups; operators see only their own
// (matched by op_id). Mirrors /api/groupacc's role-scoping rules so
// the picker doesn't even surface a group the server will reject.
function getAvailableGroups(state: TimestopState): Array<{ id: string; name: string }> {
  const role = state.currentUser?.role;
  const opId = state.currentUser?.op_id;
  if (role === 'operator') {
    // Locked to their own group. If op_id is missing/0, surface
    // nothing — the page will show the "no groups" placeholder.
    if (!opId) return [];
    const own = (state.groups || []).find(
      (g) => String(g.id) === String(opId),
    );
    return own ? [{ id: String(own.id), name: own.name || ('Group ' + own.id) }] : [];
  }
  return (state.groups || []).map((g) => ({
    id: String(g.id),
    name: g.name || ('Group ' + g.id),
  }));
}

// ---- Range fetch ----
async function ensureRangeCached(
  oid: string,
  fromYmd: string,
  toYmd: string,
  config: ApiFetchConfig,
): Promise<void> {
  const cache = getState().cache;
  // Figure out which dates we don't have. We always refetch the
  // entire range (the server already coalesces by dated so this is
  // still one call per range change).
  const reqFrom = fromYmd;
  const reqTo   = toYmd;
  setSpinnerMessage('Loading group history…');
  showSpinner('Loading group history…');
  try {
    const r = await apiFetch(
      'groupacc?oid=' + encodeURIComponent(oid) +
        '&from=' + encodeURIComponent(reqFrom) +
        '&to='   + encodeURIComponent(reqTo),
      { method: 'GET' },
      config,
    );
    if (!r.ok || !r.data || typeof r.data !== 'object') {
      throw new Error(((r.data as any)?.error as string) || 'fetch_failed');
    }
    const payload = r.data as {
      records?: Array<{ dated: string; acc_diff: unknown; mid_count?: number; games_by_mid?: Record<string, unknown> }>;
    };
    for (const rec of payload.records || []) {
      if (!rec || !rec.dated) continue;
      // Server already returns a NormalisedAcc-shaped object, but
      // run it through normaliseAccounting() so the same defensive
      // path the per-terminal Ranged tab uses applies.
      const norm = normaliseAccounting(rec.acc_diff);
      if (norm) {
        // Phase 12.14b: also cache the per-day mid_count (the
        // number of distinct terminals that contributed to the
        // bucket) so the chart code can render the
        // "active terminals per day" line without re-fetching.
        // Phase 12.14c: also cache per-mid out counts so the
        // "Token out per day" chart can show one line per
        // terminal (sum of 3 hopper counts) without N+1 calls.
        // Per-mid games played (3 slots), so the group charts can draw
        // one bar per terminal without re-fetching.
        const gamesByMid: Record<string, number[]> = {};
        if (rec.games_by_mid && typeof rec.games_by_mid === 'object') {
          for (const [midKey, slots] of Object.entries(rec.games_by_mid)) {
            if (!Array.isArray(slots)) continue;
            gamesByMid[midKey] = [0, 1, 2].map((i) => Number((slots as unknown[])[i] || 0));
          }
        }
        cache.set(rec.dated, {
          acc: norm,
          midCount: typeof rec.mid_count === 'number' ? rec.mid_count : 0,
          gamesByMid,
        });
      }
    }
    getState().lastFetchedAt = new Date().toISOString();
  } finally {
    hideSpinner();
  }
}

// ---- Calendar render (CSS class reuse with per-terminal Ranged tab) ----
function monthName(s: string): string {
  const d = parseYmd(s + 'T00:00:00');
  return d.toLocaleString('en', { month: 'long', year: 'numeric' });
}
const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function renderMonth(
  parent: HTMLElement,
  monthYmd: string,
  rs: GroupRangedState,
  minYmdAllowed: string,
  maxYmdAllowed: string,
  onClickDate: (d: string) => void,
): void {
  const m = parseYmd(monthYmd + 'T00:00:00');
  const year = m.getFullYear();
  const mon  = m.getMonth();
  const firstDow = (new Date(year, mon, 1).getDay() + 6) % 7; // Mon-first
  const daysInMonth = new Date(year, mon + 1, 0).getDate();

  const monthDiv = document.createElement('div');
  monthDiv.className = 'flex-1 min-w-0';

  const title = document.createElement('div');
  title.className = 'text-center font-medium text-gray-700 mb-2';
  title.textContent = monthName(monthYmd);
  monthDiv.appendChild(title);

  // DOW header (reuse the per-terminal Ranged tab's class — these
  // are generic Tailwind classes, not Ranged-specific).
  const dowRow = document.createElement('div');
  dowRow.className = 'grid grid-cols-7 gap-1 text-[10px]  text-gray-300 bg-gray-500 text-center mb-1 rounded-md p-1';
  for (const d of DOW) {
    const c = document.createElement('div');
    c.textContent = d;
    dowRow.appendChild(c);
  }
  monthDiv.appendChild(dowRow);

  // Day grid
  const grid = document.createElement('div');
  grid.className = 'grid grid-cols-7 gap-0  bg-gray-100 rounded-md p-1';
  for (let i = 0; i < firstDow; i++) {
    const blank = document.createElement('div');
    grid.appendChild(blank);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const cell = document.createElement('button');
    cell.type = 'button';
    const ds = year + '-' + pad2(mon + 1) + '-' + pad2(day);
    const isMin = cmpYmd(ds, minYmdAllowed) < 0;
    const isMax = cmpYmd(ds, maxYmdAllowed) > 0;
    const disabled = isMin || isMax;
    // Same 5-state range pick model as accounting-ranged.ts. CSS
    // classes are shared (.cal-cell, .cal-cell--{enabled,disabled,
    // picked,start,single,mid,end}) so the per-terminal Ranged tab
    // and the page look identical.
    const from = rs.from, to = rs.to;
    const isPicked = !!from && !to && from === ds;
    const hasRange = !!(from && to);
    const isSingle = hasRange && from === to && from === ds;
    const isStart  = hasRange && !isSingle && from === ds;
    const isEnd    = hasRange && !isSingle && to   === ds;
    const isMid    = hasRange && !isSingle && !isStart && !isEnd
                     && cmpYmd(ds, from) > 0 && cmpYmd(ds, to) < 0;
    cell.textContent = String(day);
    cell.dataset.date = ds;
    cell.className = 'cal-cell'
      + (disabled            ? ' cal-cell--disabled' : ' cal-cell--enabled')
      + (isPicked            ? ' cal-cell--picked cal-cell--start' : '')
      + (isSingle            ? ' cal-cell--single'   : '')
      + (isStart && !isPicked ? ' cal-cell--start'   : '')
      + (isMid               ? ' cal-cell--mid'      : '')
      + (isEnd               ? ' cal-cell--end'      : '');
    if (disabled) cell.disabled = true;
    if (!disabled) {
      cell.addEventListener('click', () => onClickDate(ds));
    }
    grid.appendChild(cell);
  }
  monthDiv.appendChild(grid);
  parent.appendChild(monthDiv);
}

// ---- Public entry: render the Accounting page ----
export async function renderAccountingGroup(
  state: TimestopState,
  container: HTMLElement,
  config: ApiFetchConfig,
): Promise<void> {
  // Phase 12.14b: release any prior chart instances before
  // wiping the container. Without this, every navigate-back to
  // the page (or every range change that re-runs the function)
  // would orphan the old Chart.js instances — their canvases
  // get pulled from the DOM by container.textContent = '' but
  // the JS objects stay alive in module-scope and in Chart.js's
  // internal registry, leaking memory + listeners.
  destroyGroupCharts();
  container.textContent = '';
  const rs = getState();

  // Top-of-page chrome: title + group picker. The title sits in its
  // own block, the picker drops to a new line below it (per request),
  // and the "almost white" panel that wraps the picker + the rest of
  // the page chrome (calendar, status, table) starts at the picker.
  const header = document.createElement('div');
  header.className = 'pt-6 pb-0';

  const titleWrap = document.createElement('div');
  const titleH = document.createElement('h2');
  titleH.className = 'text-3xl font-semibold';
  titleH.textContent = i18n('accounting_group.title');
  const titleP = document.createElement('p');
  titleP.className = 'text-gray-300 text-sm mt-1';
  titleP.textContent = i18n('accounting_group.subtitle');
  titleWrap.appendChild(titleH);
  titleWrap.appendChild(titleP);
  header.appendChild(titleWrap);

  // Group picker (admin sees all groups; operator sees only their own).
  // The picker sits on its own new line under the title, NOT inline
  // with the title — the user asked for a stack: title block, then
  // the picker row, then the rest of the page chrome.
  const groups = getAvailableGroups(state);
  const pickerWrap = document.createElement('div');
  pickerWrap.className = 'flex items-center gap-x-2 mt-4';
  const pickerLbl = document.createElement('label');
  pickerLbl.className = 'text-sm text-gray-500';
  pickerLbl.textContent = i18n('accounting_group.group_label');
  pickerLbl.htmlFor = 'accounting-group-picker';
  const picker = document.createElement('select');
  picker.id = 'accounting-group-picker';
  picker.className = 'border rounded-2xl px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500';
  if (groups.length === 0) {
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = i18n('common.no_groups');
    picker.appendChild(opt);
    picker.disabled = true;
  } else {
    for (const g of groups) {
      const opt = document.createElement('option');
      opt.value = g.id;
      opt.textContent = g.name + ' (#' + g.id + ')';
      picker.appendChild(opt);
    }
    // Default selection: the previously-picked group, or the first
    // available one.
    if (rs.oid && groups.some((g) => g.id === rs.oid)) {
      picker.value = rs.oid;
    } else {
      rs.oid = groups[0].id;
      picker.value = rs.oid;
    }
  }
  picker.addEventListener('change', () => {
    const v = picker.value;
    if (!v) return;
    if (v !== rs.oid) {
      // Group changed -> wipe cache + range. Different group's
      // history has different YYYY-MM-DD keys.
      rs.oid = v;
      rs.from = null;
      rs.to = null;
      rs.cache = new Map();
    }
    void renderAccountingGroup(state, container, config);
  });
  pickerWrap.appendChild(pickerLbl);
  pickerWrap.appendChild(picker);
  // The picker is the FIRST child of the white panel. Title and
  // subtitle stay OUTSIDE the panel so the page title reads on the
  // normal gray-400 background and the picker-onwards sits on its
  // own "almost white" card.
  container.appendChild(header);

  // Bail early if no group is available (operator with no op_id, or
  // the admin somehow has zero groups).
  if (!rs.oid) {
    const empty = document.createElement('div');
    empty.className = 'bg-white rounded-2xl p-6 text-gray-400 text-sm text-center mt-4';
    empty.textContent = i18n('accounting_group.no_groups_available');
    container.appendChild(empty);
    return;
  }

  // ---- "Almost white" panel: picker row + calendar + status + table ----
  // bg-white with a soft rounded-2xl + padding + drop shadow so the
  // card visibly separates from the gray-400 page background. Inner
  // content keeps its own vertical spacing via space-y-4.
  const panel = document.createElement('div');
  panel.className = 'bg-white rounded-2xl shadow-sm p-5 mt-4 space-y-4';
  panel.appendChild(pickerWrap);

  // ---- Calendar (2 months side-by-side) + status row ----
  const today = ymd(new Date());
  // Lower bound: 90 days back. We don't have a per-group "earliest
  // activity" column; the per-terminal t.ts_crea would need a
  // MIN(ts_crea) walk that adds nothing visually.
  const lower = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return ymd(d);
  })();

  const wrapper = document.createElement('div');
  // space-y-4 spaces this wrapper's DIRECT children (top bar,
  // calendar, status row, table). The outer panel's space-y-4
  // separately spaces the panel's children (picker, this wrapper)
  // — the two don't collide.
  wrapper.className = 'space-y-4';

  // Top bar: prev/next month + selected range label.
  const top = document.createElement('div');
  top.className = 'flex items-center justify-between mx-1';
  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'px-3 py-1.5 rounded-xl text-sm bg-gray-100 hover:bg-gray-200';
  prev.textContent = i18n('accounting_group.prev_month');
  prev.addEventListener('click', () => {
    rs.leftMonth = addMonths(rs.leftMonth, -1);
    void renderAccountingGroup(state, container, config);
  });
  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'px-3 py-1.5 rounded-xl text-sm bg-gray-100 hover:bg-gray-200';
  next.textContent = i18n('accounting_group.next_month');
  next.addEventListener('click', () => {
    rs.leftMonth = addMonths(rs.leftMonth, 1);
    void renderAccountingGroup(state, container, config);
  });
  const rangeLbl = document.createElement('div');
  rangeLbl.className = 'text-sm text-gray-600 font-mono';
  // MM-DD labels (no year) — same shape as the per-terminal
  // Ranged tab's range label so the two views read the same way.
  const fmtMmDd = (s: string): string => s.slice(5);
  if (rs.from && rs.to) rangeLbl.textContent = fmtMmDd(rs.from) + '  →  ' + fmtMmDd(rs.to);
  else if (rs.from)     rangeLbl.textContent = i18n('accounting_group.from_n', { date: fmtMmDd(rs.from) });
  else                  rangeLbl.textContent = i18n('accounting_group.click_start');
  top.appendChild(prev);
  top.appendChild(rangeLbl);
  top.appendChild(next);
  wrapper.appendChild(top);

  // Calendar
  const cal = document.createElement('div');
  cal.className = 'flex gap-4 bg-gray-300 rounded-2xl p-3';
  const leftMonth  = rs.leftMonth;
  const rightMonth = addMonths(leftMonth, 1);

  const onClickDate = (ds: string): void => {
    if (cmpYmd(ds, lower) < 0 || cmpYmd(ds, today) > 0) return;
    if (!rs.from || (rs.from && rs.to)) {
      rs.from = ds;
      rs.to   = null;
    } else if (rs.from && !rs.to) {
      if (ds === rs.from) {
        rs.to = rs.from;          // same-day single range
      } else if (cmpYmd(ds, rs.from) < 0) {
        rs.from = ds;             // click is before start -> new start
      } else {
        rs.to = ds;
      }
    }
    void renderAccountingGroup(state, container, config);
  };

  renderMonth(cal, leftMonth,  rs, lower, today, onClickDate);
  renderMonth(cal, rightMonth, rs, lower, today, onClickDate);
  wrapper.appendChild(cal);

  // Status row: legend + clear button + fetch badge.
  const status = document.createElement('div');
  status.className = 'flex items-center justify-between text-xs text-gray-500';
  const legend = document.createElement('div');
  legend.className = 'flex items-center gap-x-3';
  legend.innerHTML =
    '<span class="inline-flex items-center gap-x-1">' +
      '<span class="inline-block w-3 h-3 rounded bg-emerald-200"></span>' + i18n('accounting_group.legend_in_range') +
    '</span>';
  status.appendChild(legend);

  const right = document.createElement('div');
  right.className = 'flex items-center gap-x-3';
  if (rs.lastFetchedAt) {
    const fb = document.createElement('span');
    fb.textContent = i18n('accounting_group.fetched_at', { time: new Date(rs.lastFetchedAt).toLocaleTimeString() });
    right.appendChild(fb);
  }
  if (rs.from || rs.to) {
    const clr = document.createElement('button');
    clr.type = 'button';
    clr.className = 'px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200';
    clr.textContent = i18n('accounting_group.clear_range');
    clr.addEventListener('click', () => {
      rs.from = null;
      rs.to = null;
      void renderAccountingGroup(state, container, config);
    });
    right.appendChild(clr);
  }
  status.appendChild(right);
  wrapper.appendChild(status);

  // Table host
  const tableHost = document.createElement('div');
  wrapper.appendChild(tableHost);
  panel.appendChild(wrapper);
  container.appendChild(panel);

  if (rs.from && rs.to) {
    // Make sure the cache covers [from..to].
    const from = minYmd(rs.from, rs.to);
    const to   = maxYmd(rs.from, rs.to);
    try {
      await ensureRangeCached(rs.oid, from, to, config);
    } catch (e) {
      const errBox = document.createElement('div');
      errBox.className = 'bg-red-50 text-red-700 text-sm rounded-2xl p-4';
      errBox.textContent = i18n('accounting_group.failed_to_load', { message: (e as Error).message });
      tableHost.appendChild(errBox);
      return;
    }

    // Walk the range, collect cached entries, sum.
    // We pull FOUR parallel arrays: accs (for the table + chart
    // series), midCounts (for the active-terminals chart),
    // outByMids (per-mid 3-hopper slots, for the per-terminal
    // token-out line chart), and dates (so chart x-axis labels
    // are real YYYY-MM-DD strings).
    const accs: NormalisedAcc[] = [];
    const midCounts: number[] = [];
    const gamesByMids: Record<string, number[]>[] = [];
    const dates: string[] = [];
    let cur = from;
    while (cmpYmd(cur, to) <= 0) {
      const got = rs.cache.get(cur);
      if (got) {
        accs.push(got.acc);
        midCounts.push(got.midCount);
        gamesByMids.push(got.gamesByMid);
        dates.push(cur);
      }
      cur = addDays(cur, 1);
    }
    if (accs.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'bg-gray-100 rounded-2xl p-6 text-gray-400 text-sm text-center';
      empty.textContent = i18n('accounting_group.no_data_in_range');
      tableHost.appendChild(empty);
      return;
    }
    const summed = sumAccsFromAcc(accs);
    // MM-DD (no year) range label to match the per-terminal Ranged
    // tab's "since 07-01 → 07-08 (8 days)" header.
    summed.st = from.slice(5) + ' → ' + to.slice(5) + ' (' + accs.length + ' ' + (accs.length === 1 ? i18n('common.day') : i18n('common.days')) + ')';
    const inner = document.createElement('div');
    inner.className = 'space-y-4';
    tableHost.appendChild(inner);
    inner.appendChild(makeAccHeader(i18n('accounting_group.group_total'), summed.st));
    // Build a synthetic "group context" with the union of every
    // terminal's currency lookup PLUS every ISO that actually
    // appears in the summed data, so renderAccTable() shows one
    // column per ISO in use across the whole group (including
    // currencies the terminals never formally configured but had
    // historical sales in).
    const groupCtx = buildGroupContext(state, rs.oid, summed);
    renderAccTable(inner, groupCtx, summed, state);
    // Phase 12.14b: charts below the table. destroyGroupCharts()
    // was already called at the top of renderAccountingGroup(), so
    // we just paint fresh instances here.
    // Phase 12.14c: per-terminal line chart needs the list of
    // terminals in this group (drives the legend labels) plus
    // the per-day per-mid out counts (already in outByMids).
    const groupTerminals = (Object.values(state.terminals) as Terminal[]).filter(
      (t) => String(t.oid || '') === String(rs.oid),
    );
    renderGroupCharts(inner, accs, dates, midCounts, gamesByMids, groupTerminals);
  } else {
    // No range yet -- placeholder.
    const ph = document.createElement('div');
    ph.className = 'bg-gray-100 rounded-2xl p-6 text-gray-400 text-sm text-center';
    ph.textContent = i18n('accounting_group.pick_range');
    tableHost.appendChild(ph);
  }
}
